import React, { useEffect, useState } from "react";
import { Spin, Tabs, Form, Select } from "antd";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  CreateAccountTypeApi,
  GetAllAccountTypeApi,
} from "../../../api/request/account.js";
import ModalComponent from "../../../components/modals/ModalComponent.jsx";
import toast from "react-hot-toast";
import LoadableButton from "../../../components/buttons/LoadableButton.jsx";
import Copy from "../../../components/copy/Copy.jsx";
import { getEnv } from "../../../utils/utils.js";
import MySegmented from "../../../components/segmented/Segmented.jsx";

const MobileAccountTypes = () => {
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { userDetails } = useSelector((state) => state.user);
  // Default selected tab will be determined after loading accountTypes
  const [selectedTab, setSelectedTab] = useState(null);
  const [accountTypes, setAccountTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(false);
  const [createdDemoAccount, setCreatedDemoAccount] = useState(null);
  const [demoInitialValues, setDemoInitialValues] = useState({
    depositAmount: null,
    leverage: null,
  });
  const [isOpenDemoForm, setIsOpenDemoForm] = useState(false);
  const [isOpenDemoAccountSuccess, setIsOpenDemoAccountSuccess] =
    useState(false);
  const [isDemoFormLoading, setIsDemoFormLoading] = useState(false);
  const [liveInitialValues, setLiveInitialValues] = useState({
    leverage: null,
  });
  const [isOpenLiveForm, setIsOpenLiveForm] = useState(false);
  const [isLiveFormLoading, setIsLiveFormLoading] = useState(false);
  const [depositOptions, setDepositOptions] = useState([
    { value: 1000, label: "1000" },
    { value: 3000, label: "3000" },
    { value: 5000, label: "5000" },
    { value: 50000, label: "50000" },
    { value: 100000, label: "100000" },
  ]);
  const [leverageOptions, setLeverageOptions] = useState([
    { value: 20, label: "20" },
    { value: 50, label: "50" },
    { value: 100, label: "100" },
    { value: 200, label: "200" },
    { value: 300, label: "300" },
    { value: 400, label: "400" },
    { value: 500, label: "500" },
    { value: 1000, label: "1000" },
    { value: 2000, label: "2000" },
  ]);


  const name = getEnv("REACT_APP_NAME", "Neptunefxcrm");
  const SearchParamsType = searchParams?.get("type");

  const getAccounts = async () => {
    try {
      setIsLoading(true);
      let params = {
        UserId: userDetails?.id,
        tradingAccountType: searchParams?.get("type") == "live" ? 0 : 1,
      };
      const { data } = await GetAllAccountTypeApi(params);
      if (data?.success) {
        // Sort so Silver Account is first for live accounts
        let sorted = data?.data;
        if (searchParams?.get("type") === "live") {
          sorted = [
            ...data?.data.filter((item) => item.planName === "Silver Account"),
            ...data?.data.filter((item) => item.planName !== "Silver Account"),
          ];
        }
        setAccountTypes(sorted);
        // Default selection: prefer "Silver Account" if present, else first plan name
        const defaultTab =
          sorted?.find((item) => item.planName === "Silver Account")?.planName ||
          sorted?.[0]?.planName ||
          null;
        setSelectedTab(defaultTab);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getAccounts();
  }, []);

  const features = [
    { firstValue: "$", label: "Minimum Deposit", key: "minimumDeposit" },
    { label: "Instruments", key: "instruments" },
    { label: "Spread From", key: "spread", lastValue: "Pips" },
    // { label: "Commission", key: "commission", lastValue: "$" },
    { label: "Swap free/Islamic", key: "swapFree" },
    // { label: "Margin Call", key: "marginCall", lastValue: "%" },
    // { label: "Stop Out", key: "stopOut", lastValue: "%" },
    // { label: "Platform", key: "planName" },
  ];

    const featuresDemo = [
    { label: "Instruments", key: "instruments" },
    { label: "Spread From", key: "spread", lastValue: "Pips" },
    { label: "Swap free/Islamic", key: "swapFree" },
    // { label: "Margin Call", key: "marginCall", lastValue: "%" },
    // { label: "Stop Out", key: "stopOut", lastValue: "%" },
    // { label: "Platform", key: "planName" },
  ];

  const createAccount = (account) => {
    setSelectedAccount(account);
    if (searchParams?.get("type") == "live") {
      setIsOpenLiveForm(true);
      return;
    }
    if (searchParams?.get("type") == "demo") {
      setIsOpenDemoForm(true);
      return;
    }
  };

  const handleDemoAccount = async (values) => {
    try {
      setIsDemoFormLoading(true);
      let params = {
        balanceDeposit: values?.depositAmount,
        planId: selectedAccount?.planId,
        userId: userDetails?.id,
        tradingAccountType: 1,
      };
      const { data } = await CreateAccountTypeApi(params);
      if (data?.success) {
        setCreatedDemoAccount(data?.data);
        toast?.success(data?.message);
        setIsOpenDemoAccountSuccess(true);
      }
    } catch (error) {
      if (error?.response?.data?.message) {
        toast.error(error?.response?.data?.message);
      }
      setIsDemoFormLoading(false);
    } finally {
      setIsDemoFormLoading(false);
    }
  };

  const handleLiveAccount = async (values) => {
    try {
      setIsLiveFormLoading(true);
      let params = {
        balanceDeposit: 0,
        planId: selectedAccount?.planId,
        userId: userDetails?.id,
      };
      const { data } = await CreateAccountTypeApi(params);
      if (data?.success) {
        setIsLiveFormLoading(false);
        toast?.success(data?.message);
        navigate("/accounts?filter=Live+Account");
      }
    } catch (error) {
      if (error?.response?.data?.message) {
        toast.error(error?.response?.data?.message);
      }
      setIsLiveFormLoading(false);
    } finally {
      setIsLiveFormLoading(false);
    }
  };

  const goToDashboard = () => {
    setIsOpenDemoAccountSuccess(false);
    navigate("/dashboard");
  };

  useEffect(() => {
    form.resetFields();
  }, [isOpenDemoForm, isOpenLiveForm]);

  // Empty state component
  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center min-h-[400px] px-6 py-8">
      {/* Illustration */}
      <div className="mb-8">
        <svg
          width="200"
          height="150"
          viewBox="0 0 200 150"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Background elements */}
          <circle cx="40" cy="40" r="15" fill="#E8F4FD" opacity="0.6" />
          <circle cx="160" cy="30" r="10" fill="#FFE8D6" opacity="0.8" />
          <circle cx="170" cy="120" r="12" fill="#E8F4FD" opacity="0.5" />

          {/* Main illustration container */}
          <rect
            x="30"
            y="50"
            width="140"
            height="80"
            rx="8"
            fill="#F8FAFC"
            stroke="#E2E8F0"
            strokeWidth="2"
          />

          {/* Search bar */}
          <rect x="45" y="65" width="110" height="12" rx="6" fill="#E2E8F0" />
          <circle cx="145" cy="71" r="3" fill="#94A3B8" />

          {/* Document/folder icon */}
          <rect x="60" y="85" width="30" height="25" rx="3" fill="#FF6B6B" />
          <rect x="65" y="90" width="20" height="3" rx="1.5" fill="white" />
          <rect x="65" y="95" width="15" height="2" rx="1" fill="white" />
          <rect x="65" y="98" width="18" height="2" rx="1" fill="white" />

          {/* "NO DATA" text on folder */}
          <text
            x="75"
            y="105"
            fontSize="6"
            fill="white"
            textAnchor="middle"
            fontWeight="bold"
          >
            NO DATA
          </text>

          {/* Progress bars */}
          <rect x="100" y="85" width="60" height="4" rx="2" fill="#3B82F6" />
          <rect x="100" y="92" width="40" height="4" rx="2" fill="#E2E8F0" />
          <rect x="100" y="99" width="50" height="4" rx="2" fill="#E2E8F0" />

          {/* Magnifying glass */}
          <circle
            cx="155"
            cy="35"
            r="8"
            fill="none"
            stroke="#FF6B6B"
            strokeWidth="2"
          />
          <line
            x1="161"
            y1="41"
            x2="168"
            y2="48"
            stroke="#FF6B6B"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <line
            x1="150"
            y1="30"
            x2="160"
            y2="40"
            stroke="#FF6B6B"
            strokeWidth="2"
            strokeLinecap="round"
          />

          {/* Decorative elements */}
          <circle cx="25" cy="100" r="3" fill="#FFB84D" />
          <circle cx="180" cy="80" r="4" fill="#4ECDC4" />

          {/* Settings gear */}
          <g transform="translate(150, 10)">
            <circle
              cx="8"
              cy="8"
              r="6"
              fill="none"
              stroke="#FFB84D"
              strokeWidth="1.5"
            />
            <circle cx="8" cy="8" r="2" fill="#FFB84D" />
            <line
              x1="8"
              y1="2"
              x2="8"
              y2="4"
              stroke="#FFB84D"
              strokeWidth="1"
            />
            <line
              x1="8"
              y1="12"
              x2="8"
              y2="14"
              stroke="#FFB84D"
              strokeWidth="1"
            />
            <line
              x1="14"
              y1="8"
              x2="12"
              y2="8"
              stroke="#FFB84D"
              strokeWidth="1"
            />
            <line
              x1="4"
              y1="8"
              x2="2"
              y2="8"
              stroke="#FFB84D"
              strokeWidth="1"
            />
          </g>
        </svg>
      </div>

      {/* Text content */}
      <div className="text-center">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">
          You have no Demo Account
        </h3>
        <p className="text-gray-500 text-sm leading-relaxed">
          To get started, you'll need to/create an account
        </p>
      </div>
    </div>
  );

  return (
    <>
      {isLoading ? (
        <div className="flex justify-center flex-col my-[20%]">
          <Spin size="large" />
          <p className="primary text-center mt-2">Loading...</p>
        </div>
      ) : accountTypes?.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="mobile-account-types min-h-screen flex flex-col">
          {/* Content */}
          <div className="flex-1">
            {/* Account Type Tabs */}
            <div className="mb-6 bg-white rounded-lg shadow-sm py-2 px-3">
              <Tabs
                activeKey={selectedTab ?? accountTypes?.[0]?.planName}
                onChange={setSelectedTab}
                centered
                size="large"
                items={accountTypes?.map((type) => ({
                  key: type?.planName,
                  label: type?.planName?.split("Account"),
                }))}
                className="custom-tabs mobile-account-tabs"
              />
            </div>
            {/* <MySegmented
                options={accountTypes?.map((type) => ({
                  label: type?.planName,
                  value: type?.planName,
                }))}
                value={selectedTab}
                setValue={setSelectedTab}
                className="!w-full"
              /> */}

            {/* Selected Account Type Title */}
            <div className="text-center mb-6">
              <div className="flex flex-col items-center">
                <h2 className="text-xl font-bold bg-gradient-to-r from-[#155DFC] to-[#193CB8] bg-clip-text text-transparent">
                  {selectedTab}
                </h2>
                <div className="h-1 w-14 mt-2 rounded-full bg-gradient-to-r from-[#51A2FF] to-[#155DFC]"></div>
              </div>
            </div>

            {/* Features List */}
            <div className="space-y-3 mb-8">
              {(SearchParamsType === "live" ? features : featuresDemo)?.map((feature, index) => {
                const feat = accountTypes?.find(
                  (data) => data.planName === selectedTab
                );
                return (
                  <div
                    key={index}
                    className="bg-white rounded-2xl p-4 flex items-center justify-between shadow-sm bg-gradient-to-tl from-[#F0FDF4] to-[#ECFDF5]"
                  >
                    <div className="flex items-center">
                      <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-3">
                        <svg
                          className="w-4 h-4 text-white"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <span className="text-gray-700 font-medium">
                        {feature?.label}
                      </span>
                    </div>
                    <span className="text-gray-900 font-semibold">
                      {feature?.firstValue
                        ? `${feature?.firstValue}${
                            typeof feat?.[feature.key] === "boolean"
                              ? feat?.[feature.key]
                                ? "YES"
                                : "NO"
                              : feat?.[feature.key]
                          }`
                        : feature?.lastValue
                        ? `${
                            typeof feat?.[feature.key] === "boolean"
                              ? feat?.[feature.key]
                                ? "YES"
                                : "NO"
                              : feat?.[feature.key]
                          }${feature?.lastValue}`
                        : typeof feat?.[feature.key] === "boolean"
                        ? feat?.[feature.key]
                          ? "YES"
                          : "NO"
                        : feat?.[feature.key]}
                      {/* {feature?.firstValue
                        ? `${feature?.firstValue}${feat?.[feature.key]}`
                        : feature?.lastValue
                        ? `${feat?.[feature.key]}${feature?.lastValue}`
                        : feat?.[feature.key]} */}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Open Live Account Button */}
            <button
              className="w-full bg-[#193CB8] text-white py-3 rounded-2xl font-semibold text-lg shadow-lg hover:bg-[#193CB8] transition-colors"
              onClick={() =>
                createAccount(
                  accountTypes?.find((data) => data.planName === selectedTab)
                )
              }
            >
              Open {searchParams?.get("type") === "live" ? "Live" : "Demo"}{" "}
              Account
            </button>
          </div>
        </div>
      )}

      {/* Open Demo Account */}
      <ModalComponent
        isOpen={isOpenDemoForm}
        setIsOpen={setIsOpenDemoForm}
        title="Are you sure you want to open a Demo account?"
      >
        <div className="mb-3">
          <p>
            Are you sure you want to open a demo account? Please select the
            leverage for your account.
          </p>
        </div>
        <Form
          className="w-full"
          autoComplete="off"
          form={form}
          initialValues={demoInitialValues}
          onFinish={handleDemoAccount}
        >
          <div className="ant-select-selector-white ant-select-selector-mobile">
            <Form.Item
              name="depositAmount"
              rules={[{ required: true, message: "Please select deposit." }]}
            >
              <Select
                className="border-[#193CB838] w-full mt-1"
                placeholder="Select deposit"
                options={depositOptions}
              />
            </Form.Item>
          </div>
          {/* <div className="ant-select-selector-white mt-5 ant-select-selector-mobile">
            <Form.Item
              name="leverage"
              rules={[{ required: true, message: "Please select leverage." }]}
            >
              <Select
                className="border-[#193CB838] w-full mt-1"
                placeholder="Select leverage"
                options={leverageOptions}
              />
            </Form.Item>
          </div> */}
          <div className="flex justify-center items-center gap-5 mt-10">
            <button
              type="button"
              disabled={isDemoFormLoading}
              className="text-sm white primary font-bold uppercase px-8 py-3 rounded-lg border border-[#193CB838] duration-500"
              onClick={() => setIsOpenDemoForm(false)}
            >
              Cancel
            </button>
            <LoadableButton
              className="bg-primary-mobile text-sm text-white font-semibold rounded-lg px-12 py-3 uppercase themeHover duration-500"
              disabled={isDemoFormLoading}
              type="Submit"
              lable="Proceed"
              isLoading={isDemoFormLoading}
              loadingLable="Opening Demo Account..."
            />
          </div>
        </Form>
      </ModalComponent>

      {/* Demo account success modal */}
      <ModalComponent
        isOpen={isOpenDemoAccountSuccess}
        setIsOpen={setIsOpenDemoAccountSuccess}
        title="Congratulations! Demo Account Created!"
      >
        <p className="md:text-lg font-semibold">
          {userDetails?.fullName} You Have Successfully Open Vertex Account with{" "}
          {name}.
        </p>
        <div>
          <div className="flex justify-between items-center mt-8 py-2 px-4">
            <div className="flex items-center">
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-3">
                <svg
                  className="w-4 h-4 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <p className="text-sm sm:text-lg font-medium text-[#314158] ml-3">
                Account No.
              </p>
            </div>
            <div className="flex justify-center items-center">
              <p className="text-sm sm:text-lg font-medium text-[#314158] mr-2">
                {createdDemoAccount?.terminalID}
              </p>
              <Copy text={createdDemoAccount?.terminalID}></Copy>
            </div>
          </div>
          <div className="flex justify-between items-center mt-2 pb-2 px-4">
            <div className="flex items-center">
              {" "}
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-3">
                <svg
                  className="w-4 h-4 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <p className="text-sm sm:text-lg font-medium text-[#314158] ml-3">
                Server Name
              </p>
            </div>
            <p className="text-sm sm:text-lg font-medium text-[#314158] mr-2">
              {createdDemoAccount?.environmentName}
            </p>
          </div>
          <div className="flex justify-between items-center mt-2 pb-2 px-4">
            <div className="flex items-center">
              {" "}
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-3">
                <svg
                  className="w-4 h-4 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <p className="text-sm sm:text-lg font-medium text-[#314158] ml-3">
                Master Password
              </p>
            </div>
            <div className="flex justify-center items-center">
              <p className="text-sm sm:text-lg font-medium text-[#314158] mr-2">
                {createdDemoAccount?.password}
              </p>
              <Copy text={createdDemoAccount?.password}></Copy>
            </div>
          </div>
          <div className="flex justify-between items-center mt-2 pb-2 px-4">
            <div className="flex items-center">
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-3">
                <svg
                  className="w-4 h-4 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <p className="text-sm sm:text-lg font-medium text-[#314158] ml-3">
                Investor Password
              </p>
            </div>
            <div className="flex justify-center items-center">
              <p className="text-sm sm:text-lg font-medium text-[#314158] mr-2">
                {createdDemoAccount?.investorPassword}
              </p>
              <Copy text={createdDemoAccount?.investorPassword}></Copy>
            </div>
          </div>
        </div>
        <div className="mt-8 flex justify-center items-center">
          <button
            className="bg-primary-mobile text-sm text-white font-semibold px-6 py-2 rounded-lg themeHover duration-500"
            onClick={goToDashboard}
          >
            Continue
          </button>
        </div>
      </ModalComponent>

      {/* Open live account form modal */}
      <ModalComponent
        isOpen={isOpenLiveForm}
        setIsOpen={setIsOpenLiveForm}
        title="Open Live Account"
      >
        <div className="mb-3">
          <p className="text-[#1D293D]">
            Please select the leverage for your account.
          </p>
        </div>
        <Form
          className="w-full"
          autoComplete="off"
          form={form}
          initialValues={liveInitialValues}
          onFinish={handleLiveAccount}
        >
          <div className="ant-select-selector-white  ant-select-selector-mobile">
            <Form.Item
              name="leverage"
              rules={[{ required: true, message: "Please select leverage." }]}
            >
              <Select
                className="border-primary w-full mt-1"
                placeholder="Select leverage"
                options={leverageOptions}
              />
            </Form.Item>
          </div>
          <div className="flex justify-center items-center gap-5 mt-10">
            <button
              type="button"
              disabled={isLiveFormLoading}
              className="text-sm white primary font-bold uppercase px-8 py-3 rounded-lg border border-[#193CB838] duration-500"
              onClick={() => setIsOpenLiveForm(false)}
            >
              Cancel
            </button>
            <LoadableButton
              className="bg-primary-mobile text-sm text-white font-semibold rounded-lg px-12 py-3 uppercase themeHover duration-500"
              disabled={isLiveFormLoading}
              type="Submit"
              lable="Proceed"
              isLoading={isLiveFormLoading}
              loadingLable="Opening..."
            />
          </div>
        </Form>
      </ModalComponent>
    </>
  );
};

export default MobileAccountTypes;
